import { buildActivityMonitoringBookTitleMap } from './book-options';
import type { ActivityBoardItem, ActivityType, ShortBook, User } from 'library/types';

export type ActivitySummaryBreakdown = 'user' | 'project';

export type ActivitySummaryDetailRow = {
  id: number;
  label: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
};

export type ActivitySummaryRow = {
  activityTypeId: number;
  activityTypeTitle: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  detailRows: ActivitySummaryDetailRow[];
};

export type ActivitySummaryIdleOptions = {
  expectedHours: number;
  label: string;
  permissionActivityTypeIds: readonly number[];
};

export type ActivitySummaryPermissionInput = {
  assignedUserId: number;
  activityTypeId: number;
  activityTypeTitle?: string;
  effectiveHours: number;
};

export type BuildActivitySummaryRowsOptions = {
  activityBoardItems: readonly ActivityBoardItem[];
  activityTypes: readonly ActivityType[];
  activityUsers: readonly User[];
  books: readonly ShortBook[];
  breakdown: ActivitySummaryBreakdown;
  selectedUserId?: number | null;
  unknownProjectLabel: string;
  effectivePermissions?: readonly ActivitySummaryPermissionInput[];
  idle?: ActivitySummaryIdleOptions;
};

export type ActivitySummaryGrandTotal = {
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
};

export const ACTIVITY_SUMMARY_IDLE_ROW_ID = -2;
export const ACTIVITY_SUMMARY_UNKNOWN_PROJECT_ROW_ID = -1;

type Hours = {
  regularHours: number;
  overtimeHours: number;
};

const createHours = (): Hours => ({ regularHours: 0, overtimeHours: 0 });

const roundHours = (hours: number): number => Number(hours.toFixed(2));

const addItemHours = (hours: Hours, item: ActivityBoardItem) => {
  if (item.isOvertimeWork) {
    hours.overtimeHours += item.estimatedHours;
  } else {
    hours.regularHours += item.estimatedHours;
  }
};

const addRegularHours = (hours: Hours, value: number) => {
  hours.regularHours += value;
};

const getEffectivePermissionHours = (permission: ActivitySummaryPermissionInput): number =>
  Number.isFinite(permission.effectiveHours) ? Math.max(0, permission.effectiveHours) : 0;

const toDetailRow = (id: number, label: string, hours: Hours): ActivitySummaryDetailRow => {
  const regularHours = roundHours(hours.regularHours);
  const overtimeHours = roundHours(hours.overtimeHours);

  return {
    id,
    label,
    regularHours,
    overtimeHours,
    totalHours: roundHours(regularHours + overtimeHours),
  };
};

const sortDetailRows = (rows: ActivitySummaryDetailRow[]): ActivitySummaryDetailRow[] =>
  rows.sort((a, b) => b.totalHours - a.totalHours || a.label.localeCompare(b.label));

const getScopedActivityUsers = (activityUsers: readonly User[], selectedUserId?: number | null): User[] => {
  const uniqueActivityUsers = Array.from(
    new Map(activityUsers.filter((user) => user.isActivityUser).map((user) => [user.id, user])).values()
  );

  if (selectedUserId == null) return uniqueActivityUsers;

  return uniqueActivityUsers.filter((user) => user.id === selectedUserId);
};

const buildIdleRow = (
  activityBoardItems: readonly ActivityBoardItem[],
  effectivePermissions: readonly ActivitySummaryPermissionInput[],
  scopedUsers: readonly User[],
  idle: ActivitySummaryIdleOptions
): ActivitySummaryRow => {
  const hoursByUserId = new Map<number, { spentHours: number; permissionHours: number }>(
    scopedUsers.map((user) => [user.id, { spentHours: 0, permissionHours: 0 }])
  );

  for (const item of activityBoardItems) {
    const userHours = hoursByUserId.get(item.assignedUserId);
    if (!userHours) continue;

    if (!item.isOvertimeWork) userHours.spentHours += item.estimatedHours;
  }

  for (const permission of effectivePermissions) {
    const userHours = hoursByUserId.get(permission.assignedUserId);
    if (!userHours) continue;

    userHours.permissionHours += getEffectivePermissionHours(permission);
  }

  const expectedHours = Math.max(0, idle.expectedHours);
  const detailRows = sortDetailRows(
    scopedUsers.map((user) => {
      const userHours = hoursByUserId.get(user.id) ?? { spentHours: 0, permissionHours: 0 };
      const idleHours = Math.max(0, expectedHours - userHours.permissionHours - userHours.spentHours);

      return toDetailRow(user.id, user.nameSurname, { regularHours: idleHours, overtimeHours: 0 });
    })
  );
  const regularHours = roundHours(detailRows.reduce((sum, row) => sum + row.regularHours, 0));

  return {
    activityTypeId: ACTIVITY_SUMMARY_IDLE_ROW_ID,
    activityTypeTitle: idle.label,
    regularHours,
    overtimeHours: 0,
    totalHours: regularHours,
    detailRows,
  };
};

/**
 * Builds activity master rows from one canonical user-scoped item set.
 * Project details use ActivityBoardItem.bookId, matching the activity-monitoring
 * domain's existing "project" meaning. Raw permission activities are excluded
 * in favor of effective, range-clipped permission inputs. Permission activities
 * and the synthetic idle row are user-specific concepts, so neither is included
 * in project mode.
 * unknownProjectLabel groups missing and blank-title book references without
 * losing hours from the details.
 */
export const buildActivitySummaryRows = ({
  activityBoardItems,
  activityTypes,
  activityUsers,
  books,
  breakdown,
  selectedUserId,
  unknownProjectLabel,
  effectivePermissions = [],
  idle,
}: BuildActivitySummaryRowsOptions): ActivitySummaryRow[] => {
  const scopedUsers = getScopedActivityUsers(activityUsers, selectedUserId);
  const userLabelsById = new Map(scopedUsers.map((user) => [user.id, user.nameSurname]));
  const eligibleUserIds = new Set(userLabelsById.keys());
  const permissionActivityTypeIds = new Set([
    ...activityTypes.filter((activityType) => activityType.isPermissionActivity).map((activityType) => activityType.id),
    ...effectivePermissions.map((permission) => permission.activityTypeId),
    ...(idle?.permissionActivityTypeIds ?? []),
  ]);
  const knownActivityTypeIds = new Set(activityTypes.map((activityType) => activityType.id));
  const missingPermissionActivityTypes = Array.from(
    effectivePermissions
      .reduce((typesById, permission) => {
        if (!knownActivityTypeIds.has(permission.activityTypeId) && !typesById.has(permission.activityTypeId)) {
          typesById.set(permission.activityTypeId, {
            id: permission.activityTypeId,
            title: permission.activityTypeTitle?.trim() || `Activity Type #${permission.activityTypeId}`,
          });
        }
        return typesById;
      }, new Map<number, { id: number; title: string }>())
      .values()
  );
  const visibleActivityTypes =
    breakdown === 'project'
      ? activityTypes.filter((activityType) => !permissionActivityTypeIds.has(activityType.id))
      : [...activityTypes, ...missingPermissionActivityTypes];
  const activityTypeIds = new Set(visibleActivityTypes.map((activityType) => activityType.id));
  const bookTitlesById = buildActivityMonitoringBookTitleMap(books);
  const detailLabelsById = breakdown === 'user' ? userLabelsById : new Map(bookTitlesById);

  if (breakdown === 'project') {
    detailLabelsById.set(ACTIVITY_SUMMARY_UNKNOWN_PROJECT_ROW_ID, unknownProjectLabel);
  }

  const scopedItems = activityBoardItems.filter((item) => eligibleUserIds.has(item.assignedUserId));
  const canonicalActivityItems = scopedItems.filter((item) => !permissionActivityTypeIds.has(item.activityTypeId));
  const scopedEffectivePermissions = effectivePermissions.filter((permission) =>
    eligibleUserIds.has(permission.assignedUserId)
  );
  // Missing project references are reconciled through the synthetic project
  // detail row, so every eligible master hour also appears in the details.
  const eligibleItems = canonicalActivityItems.filter((item) => activityTypeIds.has(item.activityTypeId));
  const masterHoursByActivityTypeId = new Map<number, Hours>();
  const detailHoursByActivityTypeId = new Map<number, Map<number, Hours>>();

  for (const item of eligibleItems) {
    if (!masterHoursByActivityTypeId.has(item.activityTypeId)) {
      masterHoursByActivityTypeId.set(item.activityTypeId, createHours());
    }
    addItemHours(masterHoursByActivityTypeId.get(item.activityTypeId)!, item);

    const detailId =
      breakdown === 'user'
        ? item.assignedUserId
        : bookTitlesById.has(item.bookId)
          ? item.bookId
          : ACTIVITY_SUMMARY_UNKNOWN_PROJECT_ROW_ID;
    if (!detailLabelsById.has(detailId)) continue;

    if (!detailHoursByActivityTypeId.has(item.activityTypeId)) {
      detailHoursByActivityTypeId.set(item.activityTypeId, new Map());
    }

    const detailHours = detailHoursByActivityTypeId.get(item.activityTypeId)!;
    if (!detailHours.has(detailId)) detailHours.set(detailId, createHours());
    addItemHours(detailHours.get(detailId)!, item);
  }

  if (breakdown === 'user') {
    for (const permission of scopedEffectivePermissions) {
      const effectiveHours = getEffectivePermissionHours(permission);
      if (effectiveHours === 0 || !activityTypeIds.has(permission.activityTypeId)) continue;

      if (!masterHoursByActivityTypeId.has(permission.activityTypeId)) {
        masterHoursByActivityTypeId.set(permission.activityTypeId, createHours());
      }
      addRegularHours(masterHoursByActivityTypeId.get(permission.activityTypeId)!, effectiveHours);

      if (!detailHoursByActivityTypeId.has(permission.activityTypeId)) {
        detailHoursByActivityTypeId.set(permission.activityTypeId, new Map());
      }

      const detailHours = detailHoursByActivityTypeId.get(permission.activityTypeId)!;
      if (!detailHours.has(permission.assignedUserId)) {
        detailHours.set(permission.assignedUserId, createHours());
      }
      addRegularHours(detailHours.get(permission.assignedUserId)!, effectiveHours);
    }
  }

  const rows = visibleActivityTypes.map((activityType): ActivitySummaryRow => {
    const masterHours = masterHoursByActivityTypeId.get(activityType.id) ?? createHours();
    const regularHours = roundHours(masterHours.regularHours);
    const overtimeHours = roundHours(masterHours.overtimeHours);
    const detailHours = detailHoursByActivityTypeId.get(activityType.id);
    const detailRows: ActivitySummaryDetailRow[] = [];

    detailHours?.forEach((hours, detailId) => {
      const label = detailLabelsById.get(detailId);
      if (label !== undefined) detailRows.push(toDetailRow(detailId, label, hours));
    });

    return {
      activityTypeId: activityType.id,
      activityTypeTitle: activityType.title,
      regularHours,
      overtimeHours,
      totalHours: roundHours(regularHours + overtimeHours),
      detailRows: sortDetailRows(detailRows),
    };
  });

  if (breakdown === 'user' && idle) {
    // All scoped work reduces idle time, even if an activity type was removed
    // after the work was recorded and can no longer be rendered as a master row.
    rows.push(buildIdleRow(canonicalActivityItems, scopedEffectivePermissions, scopedUsers, idle));
  }

  return rows.sort((a, b) => b.totalHours - a.totalHours || a.activityTypeTitle.localeCompare(b.activityTypeTitle));
};

export const calculateActivitySummaryGrandTotal = (rows: readonly ActivitySummaryRow[]): ActivitySummaryGrandTotal => {
  const activityRows = rows.filter((row) => row.activityTypeId !== ACTIVITY_SUMMARY_IDLE_ROW_ID);
  const regularHours = roundHours(activityRows.reduce((sum, row) => sum + row.regularHours, 0));
  const overtimeHours = roundHours(activityRows.reduce((sum, row) => sum + row.overtimeHours, 0));

  return {
    regularHours,
    overtimeHours,
    totalHours: roundHours(regularHours + overtimeHours),
  };
};
